import { useEffect, useState } from "react";
import api, { apiError, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

// Dialogue de création / édition d'utilisateur avec rôle + permissions personnalisables.
function UserFormDialog({ catalog, editUser, onSaved }) {
  const [open, setOpen] = useState(false);
  const isEdit = !!editUser;
  const empty = { name: "", email: "", password: "", role: "commercial", permissions: [] };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setF({ name: editUser.name || "", email: editUser.email || "", password: "",
             role: editUser.role || "commercial", permissions: editUser.permissions || [] });
    } else {
      const role = "commercial";
      setF({ ...empty, role, permissions: catalog?.role_defaults?.[role] || [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onRole = (role) => {
    // Changer de rôle réinitialise les permissions sur les valeurs par défaut du rôle (modifiable ensuite).
    setF((p) => ({ ...p, role, permissions: catalog?.role_defaults?.[role] || [] }));
  };
  const togglePerm = (key) => setF((p) => ({
    ...p,
    permissions: p.permissions.includes(key) ? p.permissions.filter((k) => k !== key) : [...p.permissions, key],
  }));

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: f.name, role: f.role, permissions: f.permissions };
        if (f.password) payload.password = f.password;
        await api.patch(`/users/${editUser.id}`, payload);
        toast.success("Utilisateur mis à jour");
      } else {
        await api.post("/users", { name: f.name, email: f.email, password: f.password, role: f.role, permissions: f.permissions });
        toast.success("Utilisateur créé");
      }
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const roles = catalog?.roles || {};
  const perms = catalog?.permissions || {};
  const isAdminRole = f.role === "admin";
  const disabled = saving || !f.name || (!isEdit && (!f.email || f.password.length < 6));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500" data-testid={`edit-user-${editUser.email}`}><Icons.Pencil className="h-4 w-4" /></Button>
          : <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-user-btn"><Icons.UserPlus className="mr-1.5 h-4 w-4" />Nouvel utilisateur</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle>
          <DialogDescription>Choisissez un rôle puis ajustez librement les droits accordés.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nom</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></div>
          {!isEdit && <div className="space-y-1.5"><Label>E-mail</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></div>}
          <div className="space-y-1.5"><Label>{isEdit ? "Nouveau mot de passe (optionnel)" : "Mot de passe (min. 6 caractères)"}</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" /></div>
          <div className="space-y-1.5"><Label>Rôle</Label>
            <Select value={f.role} onValueChange={onRole}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(roles).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`role-opt-${k}`}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2 rounded-lg border border-slate-200 p-3">
            <p className="text-xs font-semibold uppercase text-slate-400">Droits accordés</p>
            {isAdminRole ? (
              <p className="text-sm text-slate-500">L'administrateur dispose de tous les droits.</p>
            ) : (
              <div className="space-y-1.5" data-testid="perm-list">
                {Object.entries(perms).map(([key, label]) => (
                  <label key={key} className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-1 py-1 hover:bg-slate-50">
                    <span className="text-sm text-slate-700">{label}</span>
                    <Switch checked={f.permissions.includes(key)} onCheckedChange={() => togglePerm(key)} data-testid={`perm-${key}`} />
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={disabled} data-testid="user-save" className="bg-emerald-600 hover:bg-emerald-700">{isEdit ? "Enregistrer" : "Créer"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MyAccountDialog({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", current_password: "", new_password: "" });
  useEffect(() => { if (open) setF({ name: user?.name || "", email: user?.email || "", current_password: "", new_password: "" }); }, [open, user]);
  const save = async () => {
    try {
      const payload = { name: f.name, email: f.email };
      if (f.new_password) payload.new_password = f.new_password;
      if (f.current_password) payload.current_password = f.current_password;
      const { data } = await api.patch("/auth/me", payload);
      onSaved(data);
      toast.success("Compte mis à jour");
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="edit-account-btn"><Icons.Pencil className="mr-1.5 h-4 w-4" />Modifier</Button></DialogTrigger>
      <DialogContent data-testid="account-dialog">
        <DialogHeader><DialogTitle>Modifier mon compte</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nom</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="account-name" /></div>
          <div className="space-y-1.5"><Label>E-mail</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="account-email" /></div>
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="mb-2 text-xs text-slate-500">Pour changer l'e-mail ou le mot de passe, saisissez votre mot de passe actuel.</p>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Mot de passe actuel</Label><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="account-current-password" /></div>
              <div className="space-y-1.5"><Label>Nouveau mot de passe (optionnel)</Label><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="account-new-password" /></div>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.name || !f.email} data-testid="account-save" className="bg-emerald-600 hover:bg-emerald-700">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanySettings() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoV, setLogoV] = useState(0);
  useEffect(() => { api.get("/company").then((r) => setF(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/company", { site: f.site, web: f.web, phone: f.phone, tel: f.tel, slogan: f.slogan }); toast.success("Identité entreprise mise à jour"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const onLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/company/logo", fd); toast.success("Logo mis à jour"); setF((p) => ({ ...p, has_logo: true })); setLogoV((v) => v + 1); }
    catch (err) { toast.error(apiError(err)); }
  };
  if (!f) return null;
  return (
    <Card className="p-5" data-testid="company-settings">
      <h2 className="mb-1 font-display text-lg font-semibold text-slate-800">Identité entreprise (PDF)</h2>
      <p className="mb-3 text-sm text-slate-500">Ces informations apparaissent sur tous les rapports PDF (bandeau et pied de page).</p>
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-20 w-40 items-center justify-center rounded-lg border border-slate-200 bg-[#0D2E24] p-2">
            {f.has_logo ? <img src={`${API}/company/logo?auth=${getToken()}&v=${logoV}`} alt="logo" className="max-h-full max-w-full object-contain" data-testid="company-logo-preview" /> : <span className="text-xs text-white/60">Aucun logo</span>}
          </div>
          <label className="cursor-pointer text-sm font-medium text-emerald-700">
            <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="company-logo-input" />
            <span className="inline-flex items-center gap-1"><Icons.Upload className="h-4 w-4" />Changer le logo</span>
          </label>
        </div>
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Site (affiché)</Label><Input value={f.site || ""} onChange={(e) => setF({ ...f, site: e.target.value })} data-testid="company-site" /></div>
          <div className="space-y-1.5"><Label>Lien web</Label><Input value={f.web || ""} onChange={(e) => setF({ ...f, web: e.target.value })} data-testid="company-web" /></div>
          <div className="space-y-1.5"><Label>Téléphone (affiché)</Label><Input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="company-phone" /></div>
          <div className="space-y-1.5"><Label>Lien téléphone</Label><Input value={f.tel || ""} onChange={(e) => setF({ ...f, tel: e.target.value })} placeholder="tel:+596696687766" data-testid="company-tel" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Slogan</Label><Input value={f.slogan || ""} onChange={(e) => setF({ ...f, slogan: e.target.value })} data-testid="company-slogan" /></div>
        </div>
      </div>
      <div className="mt-4"><Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="company-save">Enregistrer</Button></div>
    </Card>
  );
}

function InvoiceReminderSettings() {
  const [d, setD] = useState(null);
  const [s, setS] = useState(null);
  const load = () => api.get("/invoices-reminders/settings").then((r) => { setD(r.data); setS(r.data.settings); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const save = async (patch) => {
    const next = { ...s, ...patch };
    if (patch.enabled && !window.confirm(`Activer les rappels ?\n\n- Jour de l'échéance : « ${d.templates.echeance} »\n- ${next.after_days} jours après : « ${d.templates.apres} »\n- Envoi à ${next.hour} h (Martinique)\n\n${d.overdue_to_check.length} facture(s) déjà échue(s) listées ci-dessous : vérifiez-les avant.`)) return;
    try { await api.put("/invoices-reminders/settings", { enabled: next.enabled, hour: Number(next.hour), due_day: next.due_day, after_days: Number(next.after_days) }); toast.success("Réglages des rappels enregistrés"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-5" data-testid="invoice-reminder-settings">
      <h2 className="mb-1 font-display text-lg font-semibold text-slate-800">Factures — Rappels d'échéance</h2>
      <p className="mb-3 text-sm text-slate-500">E-mail + notification dans l'espace client, le jour de l'échéance puis après le délai choisi, uniquement si un solde subsiste. Fuseau America/Martinique. Aucun coût supplémentaire : service d'e-mail existant.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label>Heure d'envoi</Label><Input className="w-20" inputMode="numeric" value={s.hour} onChange={(e) => setS({ ...s, hour: e.target.value })} data-testid="inv-rem-hour" /></div>
        <div className="space-y-1"><Label>Relance après (jours)</Label><Input className="w-20" inputMode="numeric" value={s.after_days} onChange={(e) => setS({ ...s, after_days: e.target.value })} data-testid="inv-rem-days" /></div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={s.due_day} onCheckedChange={(v) => setS({ ...s, due_day: v })} />Rappel le jour J</label>
        <Button variant="outline" onClick={() => save({})} data-testid="inv-rem-save">Enregistrer</Button>
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm font-medium text-slate-700"><Switch checked={s.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="inv-rem-enabled" />{s.enabled ? "Rappels activés" : "Rappels désactivés (en attente de votre validation)"}</label>
      <p className="mt-2 text-xs text-slate-500">Modèles : « {d.templates.echeance} » / « {d.templates.apres} ». {d.note}</p>
      {d.overdue_to_check.length > 0 && <div className="mt-3 rounded-lg bg-amber-50 p-3 text-sm" data-testid="inv-rem-overdue"><p className="font-medium text-amber-900">Factures déjà échues à vérifier</p>
        <ul className="mt-1 space-y-0.5 text-amber-900">{d.overdue_to_check.map((o) => <li key={o.id}>{o.number} · {o.client} · {o.worksite} · échue le {o.due_date} · solde {o.due.toFixed(2).replace(".", ",")} €{o.paused ? " · rappels suspendus" : ""}</li>)}</ul></div>}
    </Card>
  );
}

function DevisReminderSettings() {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/devis/settings/reminders").then((r) => setOn(r.data.enabled)).catch(() => {}); }, []);
  const toggle = async (v) => {
    try { await api.put("/devis/settings/reminders", { enabled: v }); setOn(v); toast.success(v ? "Relances automatiques activées" : "Relances automatiques désactivées pour tous les devis"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-5" data-testid="devis-reminder-settings">
      <h2 className="mb-1 font-display text-lg font-semibold text-slate-800">Devis — Relance automatique</h2>
      <p className="mb-3 text-sm text-slate-500">Une seule relance par e-mail si le client n'a pas ouvert son devis 3 jours après la publication. Vous pouvez aussi la couper devis par devis.</p>
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <Switch checked={!!on} disabled={on === null} onCheckedChange={toggle} data-testid="devis-reminder-global" />
        {on ? "Relances automatiques activées" : "Relances automatiques désactivées"}
      </label>
    </Card>
  );
}

function CareSettings() {
  const [days, setDays] = useState("");
  useEffect(() => { api.get("/care/settings").then((r) => setDays(String(r.data.warranty_alert_days))).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/care/settings", { warranty_alert_days: Number(days) }); toast.success("Délai d'alerte enregistré"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-5" data-testid="care-settings">
      <h2 className="mb-1 font-display text-lg font-semibold text-slate-800">SIGNA CARE — Garanties</h2>
      <p className="mb-3 text-sm text-slate-500">Délai avant l'échéance d'une garantie à partir duquel « Échéance proche » est affiché et l'équipe est alertée.</p>
      <div className="flex max-w-xs items-center gap-2">
        <Input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} data-testid="care-alert-days" /><span className="text-sm text-slate-500">jours</span>
        <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="care-alert-save">Enregistrer</Button>
      </div>
    </Card>
  );
}

function CatalogSettings() {
  const [cat, setCat] = useState({ custom_prestations: [], equipments: [] });
  const [newPrest, setNewPrest] = useState("");
  const [newEquip, setNewEquip] = useState("");
  useEffect(() => { api.get("/catalog").then((r) => setCat(r.data)).catch(() => {}); }, []);

  const save = async (next) => {
    try {
      const { data } = await api.put("/catalog", { custom_prestations: next.custom_prestations, equipments: next.equipments });
      setCat(data); toast.success("Catalogue mis à jour");
    } catch (e) { toast.error(apiError(e)); }
  };
  const addPrest = () => { if (!newPrest.trim()) return; save({ ...cat, custom_prestations: [...(cat.custom_prestations || []), { name: newPrest.trim() }] }); setNewPrest(""); };
  const removePrest = (i) => save({ ...cat, custom_prestations: cat.custom_prestations.filter((_, idx) => idx !== i) });
  const addEquip = () => { if (!newEquip.trim() || (cat.equipments || []).includes(newEquip.trim())) { setNewEquip(""); return; } save({ ...cat, equipments: [...(cat.equipments || []), newEquip.trim()] }); setNewEquip(""); };
  const removeEquip = (i) => save({ ...cat, equipments: cat.equipments.filter((_, idx) => idx !== i) });

  return (
    <Card className="p-5" data-testid="catalog-settings">
      <h2 className="mb-1 font-display text-lg font-semibold text-slate-800">Prestations & équipements</h2>
      <p className="mb-4 text-sm text-slate-500">Vos prestations personnalisées et vos équipements réutilisables dans les visites SIGNA CHECK.</p>
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <Label>Prestations personnalisées</Label>
          <div className="mt-2 flex gap-2">
            <Input value={newPrest} onChange={(e) => setNewPrest(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPrest()} placeholder="Ex. Désinfection, Démoussage…" data-testid="catalog-prest-input" />
            <Button onClick={addPrest} className="bg-emerald-600 hover:bg-emerald-700" data-testid="catalog-prest-add"><Icons.Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-3 space-y-1.5">
            {(cat.custom_prestations || []).length === 0 && <p className="text-xs text-slate-400">Aucune prestation personnalisée.</p>}
            {(cat.custom_prestations || []).map((p, i) => (
              <div key={p.id || i} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`catalog-prest-${i}`}>
                <span className="text-slate-700">{p.name}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => removePrest(i)} data-testid={`catalog-prest-remove-${i}`}><Icons.Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <Label>Équipements</Label>
          <div className="mt-2 flex gap-2">
            <Input value={newEquip} onChange={(e) => setNewEquip(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addEquip()} placeholder="Ex. Nacelle, Drone…" data-testid="catalog-equip-input" />
            <Button onClick={addEquip} className="bg-emerald-600 hover:bg-emerald-700" data-testid="catalog-equip-add"><Icons.Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(cat.equipments || []).map((e, i) => (
              <span key={i} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-700" data-testid={`catalog-equip-${i}`}>
                {e}<button onClick={() => removeEquip(i)} className="text-rose-500 hover:text-rose-700" data-testid={`catalog-equip-remove-${i}`}><Icons.X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function Settings() {
  const { user, isAdmin, hasPerm, setUser } = useAuth();
  const canUsers = hasPerm("users.manage");
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState(null);

  const load = () => { if (canUsers) api.get("/users").then((r) => setUsers(r.data)); };
  useEffect(() => { load(); api.get("/permissions/catalog").then((r) => setCatalog(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [canUsers]);
  const roleLabel = (r) => catalog?.roles?.[r] || r;

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteUser = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("Utilisateur supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 lg:text-3xl">Paramètres</h1>
        <p className="text-sm text-slate-500">Votre compte et la configuration de l'application.</p>
      </div>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-slate-800">Mon compte</h2>
          <MyAccountDialog user={user} onSaved={setUser} />
        </div>
        <div className="grid gap-3 text-sm sm:grid-cols-3">
          <div><p className="text-slate-400">Nom</p><p className="font-medium text-slate-800">{user?.name}</p></div>
          <div><p className="text-slate-400">E-mail</p><p className="font-medium text-slate-800">{user?.email}</p></div>
          <div><p className="text-slate-400">Rôle</p><p className="font-medium text-slate-800">{isAdmin ? "Administrateur" : roleLabel(user?.role)}</p></div>
        </div>
      </Card>

      {canUsers && (
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 p-4">
            <h2 className="font-display text-lg font-semibold text-slate-800">Utilisateurs</h2>
            <UserFormDialog catalog={catalog} onSaved={load} />
          </div>
          <ul className="divide-y divide-slate-100">
            {users.map((u) => (
              <li key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid={`user-row-${u.email}`}>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">
                  {(u.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-800">{u.name}</p>
                  <p className="truncate text-xs text-slate-500">{u.email}</p>
                </div>
                <span className={`rounded px-2 py-0.5 text-xs font-medium ${u.role === "admin" ? "bg-[#0D2E24] text-white" : "bg-slate-100 text-slate-600"}`}>{roleLabel(u.role)}</span>
                <div className="flex items-center gap-1">
                  <Switch checked={u.is_active !== false} onCheckedChange={() => toggleActive(u)} disabled={u.id === user.id} data-testid={`toggle-user-${u.email}`} />
                  <UserFormDialog catalog={catalog} editUser={u} onSaved={load} />
                  {u.id !== user.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" data-testid={`delete-user-${u.email}`}><Icons.Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
                          <AlertDialogDescription>« {u.name} » sera supprimé définitivement. Impossible s'il est lié à des visites (désactivez le compte dans ce cas). Action irréversible.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteUser(u)} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-user-${u.email}`}>Supprimer</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {canUsers && <CompanySettings />}

      {hasPerm("visits.manage") && <CatalogSettings />}
      {hasPerm("care.manage") && <CareSettings />}
      {hasPerm("devis.manage") && <DevisReminderSettings />}
      {hasPerm("track.manage") && <InvoiceReminderSettings />}

      <Card className="p-5">
        <h2 className="mb-2 font-display text-lg font-semibold text-slate-800">Sauvegarde & stockage local</h2>
        <div className="space-y-2 text-sm text-slate-600">
          <p className="flex items-start gap-2"><Icons.Info className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            Les visites sont sauvegardées automatiquement sur le serveur. Un brouillon local est conservé sur cet appareil et resynchronisé au retour du réseau.</p>
          <p className="flex items-start gap-2"><Icons.TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            Limite : si vous effacez les données de votre navigateur, les brouillons locaux non synchronisés seront perdus. Une première connexion nécessite le réseau.</p>
        </div>
      </Card>
    </div>
  );
}
